// ────────────────────────────────────────────────────────────────────────────
// Shared grouping + color logic for the Class Calendar.
//
// The `/class-schedule/getAll` feed returns ONE ROW PER STUDENT (one student per
// class per date). For a calendar we need ONE EVENT PER CLASS OCCURRENCE, so we
// group those rows by (classId + date + start + end + staff) and collapse the
// students into a roster. Both the Class Calendar page and the Dashboard widget
// import this module so the grouping stays a single source of truth.
// ────────────────────────────────────────────────────────────────────────────

// A single row from POST /class-schedule/getAll (enriched by ClassScheduleController::index)
export interface ClassScheduleRow {
  id: number;
  classId: number;
  studentId: number;
  staffId: number;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  status: string;
  scheduleType?: string;
  staffName?: string;
  studentName?: string;
  subjectName?: string;
  gradeName?: string;
  locationName?: string;
  classMode?: string;
  assignClass?: { location_id?: number | null } | null;
}

export interface ClassRosterEntry {
  scheduleId: number;
  studentId: number;
  studentName: string;
  status: string;
}

export interface GroupedClassEvent {
  key: string;
  classId: number;
  staffId: number;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  staffName: string;
  subjectName: string;
  gradeName: string;
  locationName: string;
  locationId: number | null;
  classMode: string;
  students: ClassRosterEntry[];
  studentCount: number;
  representativeScheduleId: number;
  scheduleIds: number[];
}

// Group per-student rows into one event per class occurrence.
export const groupClassRows = (rows: ClassScheduleRow[]): GroupedClassEvent[] => {
  const map = new Map<string, GroupedClassEvent>();

  rows.forEach((row) => {
    const key = `${row.classId}|${row.scheduledDate}|${row.startTime}|${row.endTime}|${row.staffId}`;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        classId: row.classId,
        staffId: row.staffId,
        scheduledDate: row.scheduledDate,
        startTime: row.startTime,
        endTime: row.endTime,
        staffName: row.staffName || "",
        subjectName: row.subjectName || "",
        gradeName: row.gradeName || "",
        locationName: row.locationName || "",
        locationId: row.assignClass?.location_id ?? null,
        classMode: row.classMode || "",
        students: [],
        studentCount: 0,
        representativeScheduleId: row.id,
        scheduleIds: [],
      };
      map.set(key, group);
    }
    // one row = one student occurrence
    if (!group.students.some((s) => s.studentId === row.studentId)) {
      group.students.push({
        scheduleId: row.id,
        studentId: row.studentId,
        studentName: row.studentName || "",
        status: row.status || "scheduled",
      });
    }
    group.scheduleIds.push(row.id);
    group.studentCount = group.students.length;
  });

  return Array.from(map.values()).sort((a, b) => {
    if (a.scheduledDate !== b.scheduledDate) return a.scheduledDate < b.scheduledDate ? -1 : 1;
    return a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0;
  });
};

// Deterministic palette for coloring events by subject (no per-subject color in DB).
export const SUBJECT_PALETTE = [
  "#6366f1", "#0ea5e9", "#8b5cf6", "#f59e0b", "#22c55e",
  "#ef4444", "#ec4899", "#14b8a6", "#f97316", "#3b82f6",
  "#a855f7", "#10b981", "#eab308", "#06b6d4", "#f43f5e",
];

// Stable hash → palette index so the same subject always gets the same color.
export const colorForSubject = (name: string | undefined): string => {
  const key = (name || "").trim().toLowerCase();
  if (!key) return "#94a3b8";
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return SUBJECT_PALETTE[hash % SUBJECT_PALETTE.length];
};

export type ColorMode = "subject" | "location";

// Resolve the accent color for an event given the current color mode.
export const eventColor = (
  ev: GroupedClassEvent,
  mode: ColorMode,
  locationColorMap: Map<number, string>
): string => {
  if (mode === "location") {
    const c = ev.locationId != null ? locationColorMap.get(ev.locationId) : undefined;
    return c || "#54b8e8";
  }
  return colorForSubject(ev.subjectName);
};

// Human 12-hour time label, e.g. "9:00 AM".
export const formatTimeLabel = (time: string | undefined): string => {
  if (!time) return "";
  const [hour, minute] = time.split(":").map(Number);
  if (Number.isNaN(hour)) return time;
  const d = new Date();
  d.setHours(hour, minute || 0);
  return new Intl.DateTimeFormat("en-CA", { hour: "numeric", minute: "numeric", hour12: true }).format(d);
};

// Attendance/status → chip colors for the roster in the modal.
export const statusChipColors = (status: string): { bg: string; text: string } => {
  switch ((status || "").toLowerCase()) {
    case "present":
    case "completed":
      return { bg: "#dcfce7", text: "#15803d" };
    case "absent":
    case "cancelled":
      return { bg: "#fee2e2", text: "#b91c1c" };
    case "late":
      return { bg: "#fef9c3", text: "#a16207" };
    case "excused":
    case "rescheduled":
      return { bg: "#e0e7ff", text: "#4338ca" };
    default: // scheduled
      return { bg: "#e5edff", text: "#2563eb" };
  }
};
